import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Tag,
  Button,
  message,
  Tabs,
  Empty,
  Space,
  Modal,
  Form,
  Input,
  Breadcrumb,
  Badge,
  Select,
  AutoComplete,
  Spin,
  Tooltip,
} from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  FilePdfOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { certificationAPI, leaderboardAPI } from '../services/api';
import AppLayout from '../components/AppLayout';
import dayjs from 'dayjs';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const CertificationVerification = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [certifications, setCertifications] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedCert, setSelectedCert] = useState(null);
  const [verifyModalVisible, setVerifyModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [rejectForm] = Form.useForm();

  // NEW: State for manual NIM/NIP input (same as video verification)
  const [selectedRole, setSelectedRole] = useState(null);
  const [affiliateOptions, setAffiliateOptions] = useState([]);
  const [searchingAffiliates, setSearchingAffiliates] = useState(false);
  const [selectedAffiliateUnitId, setSelectedAffiliateUnitId] = useState(null);

  const loadCertifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        for_verification: 'true',  // Admin/verifier can see all certifications
      };

      if (activeTab !== 'all') {
        params.status = activeTab;
      }

      const response = await certificationAPI.list(params);

      if (response.status === 'success') {
        setCertifications(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
        }));
      }
    } catch (error) {
      console.error('Error loading certifications:', error);
      message.error('Gagal memuat daftar sertifikasi');
    } finally {
      setLoading(false);
    }
  }, [activeTab, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadCertifications();
  }, [loadCertifications]);

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPagination((prev) => ({ ...prev, current: 1 })); // Reset to page 1
  };

  const showVerifyModal = (cert) => {
    setSelectedCert(cert);
    setVerifyModalVisible(true);
  };

  const showRejectModal = (cert) => {
    setSelectedCert(cert);
    form.resetFields();
    setRejectModalVisible(true);
  };

  // NEW: Handler functions for manual NIM/NIP input
  const handleRoleChange = (value) => {
    setSelectedRole(value);
    form.setFieldsValue({ nim_nip: '', name: '', affiliate_search: '' });
    setAffiliateOptions([]);
    setSelectedAffiliateUnitId(null);
  };

  const handleAffiliateSearch = async (searchText) => {
    if (!searchText || searchText.length < 2 || !selectedRole) {
      return;
    }

    setSearchingAffiliates(true);

    try {
      let type;
      if (selectedRole === 'student') {
        type = 'mahasiswa';
      } else if (selectedRole === 'lecturer') {
        type = 'dosen';
      } else if (selectedRole === 'staff') {
        type = 'tenaga_pendidik';
      } else {
        setSearchingAffiliates(false);
        return;
      }

      const response = await leaderboardAPI.searchAffiliates(searchText, type, 20);

      if (response.status === 'success' && response.data) {
        const options = response.data.map(affiliate => ({
          value: affiliate.external_id,
          label: `${affiliate.name || affiliate.external_id} (${affiliate.external_id}) - ${affiliate.unit_code || ''}`,
          affiliate: affiliate
        }));
        setAffiliateOptions(options);

        if (options.length === 0) {
          message.info('Tidak ada data ditemukan. Anda dapat mengetik NIM/NIP dan nama secara manual.');
        }
      } else {
        setAffiliateOptions([]);
        message.warning('Tidak ada hasil pencarian. Anda dapat mengetik NIM/NIP dan nama secara manual.');
      }
    } catch (error) {
      console.error('Error searching affiliates:', error);
      message.error('Gagal mencari data. Anda dapat mengetik NIM/NIP dan nama secara manual.');
      setAffiliateOptions([]);
    } finally {
      setSearchingAffiliates(false);
    }
  };

  const handleAffiliateSelect = (value, option) => {
    if (option && option.affiliate) {
      form.setFieldsValue({
        nim_nip: option.affiliate.external_id,
        name: option.affiliate.name
      });
      setSelectedAffiliateUnitId(option.affiliate.unit_id);
    }
  };

  const handleVerify = async (values) => {
    try {
      // Build affiliate data from form
      const affiliateType = values.role === 'student' ? 'mahasiswa' : values.role === 'lecturer' ? 'dosen' : 'tenaga_pendidik';

      const affiliate = {
        type: affiliateType,
        nim_or_id: values.nim_nip,
        name: values.name || ''
      };

      // For dosen and tenaga_pendidik, unit_id is required by backend
      // Students (mahasiswa) have unit auto-detected from NIM pattern
      if (affiliateType !== 'mahasiswa' && selectedAffiliateUnitId) {
        affiliate.unit_id = selectedAffiliateUnitId;
      }

      // Call API with affiliate data
      const response = await certificationAPI.verify(selectedCert.id, 'verified', affiliate);

      if (response.status === 'success') {
        message.success(`✅ Sertifikasi berhasil diverifikasi! ${values.nim_nip} mendapat +1 poin.`, 5);
        setVerifyModalVisible(false);
        form.resetFields();
        setSelectedRole(null);
        setSelectedAffiliateUnitId(null);
        loadCertifications();
      } else {
        const errorMsg = response.message || 'Gagal memverifikasi sertifikasi';
        message.error(errorMsg, 8);
      }
    } catch (error) {
      console.error('Error verifying:', error);
      const errorMsg = error.response?.data?.message || 'Gagal memverifikasi sertifikasi';
      message.error(errorMsg, 8);
    }
  };

  const handleReject = async (values) => {
    try {
      const response = await certificationAPI.verify(
        selectedCert.id,
        'rejected',
        null, // no affiliate for rejection
        values.rejection_reason
      );
      if (response.status === 'success') {
        message.success('Sertifikasi telah ditolak');
        setRejectModalVisible(false);
        rejectForm.resetFields();
        loadCertifications();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Gagal menolak sertifikasi');
    }
  };


  const handleViewPDF = (cert) => {
    // Always open in new tab (for both URL and local files)
    if (cert.file_path && (cert.file_path.startsWith('http://') || cert.file_path.startsWith('https://'))) {
      window.open(cert.file_path, '_blank');
    } else {
      // For local files, open download endpoint in new tab
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:6543'}/api/certifications/${cert.id}/download`;
      window.open(url, '_blank');
    }
    message.success('Membuka sertifikasi di tab baru');
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: {
        color: 'orange',
        icon: <ClockCircleOutlined />,
        text: 'Menunggu Verifikasi',
      },
      verified: {
        color: '#14B8A6',
        icon: <CheckCircleOutlined />,
        text: 'Terverifikasi',
      },
      rejected: {
        color: 'red',
        icon: <CloseCircleOutlined />,
        text: 'Ditolak',
      },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return 'URL';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const columns = [
    {
      title: 'Content',
      key: 'content',
      render: (_, record) => {
        const typeLabels = {
          'google-ai-essentials-student': 'AI Essentials Student',
          'gen-ai-student': 'Gen AI Student',
          'google-ai-essentials-faculty': 'AI Essentials Faculty',
          'workspace-ai-faculty': 'Workspace AI Faculty',
          'ai-essentials-educator': 'AI Essentials Educator',
          'certified-educator': 'Certified Educator',
          'workspace-educator': 'Workspace Educator',
          'google-ai-essentials': 'AI Essentials',
          'google-cloud-ai': 'Cloud AI',
          'other': 'Lainnya',
        };

        return (
          <div style={{ minWidth: 300 }}>
            {/* Title and User */}
            <div style={{ display: 'flex', alignItems: 'start', gap: 8, marginBottom: 6 }}>
              <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '16px', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '14px', lineHeight: '1.4', marginBottom: 4 }}>
                  {record.title || 'No title'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.user?.name || record.user_id}
                  {record.user?.email && (
                    <span style={{ marginLeft: 8, color: '#999' }}>({record.user.email})</span>
                  )}
                </div>
              </div>
            </div>

            {/* Tags: Type */}
            <div style={{ marginBottom: 6 }}>
              <Tag color="blue" style={{ marginRight: 4, marginBottom: 4 }}>
                {typeLabels[record.type] || record.type}
              </Tag>
              <Tag color="cyan" style={{ marginBottom: 4 }}>
                {formatFileSize(record.file_size)}
              </Tag>
            </div>

            {/* Rejection reason for rejected status */}
            {record.status === 'rejected' && record.notes && (
              <div style={{ marginTop: 6, padding: '4px 8px', background: '#fff1f0', borderLeft: '3px solid #ff4d4f', fontSize: '12px', color: '#cf1322' }}>
                <CloseCircleOutlined /> {record.notes}
              </div>
            )}
          </div>
        );
      },
      width: '40%',
    },
    {
      title: 'Dates',
      key: 'dates',
      render: (_, record) => (
        <div style={{ minWidth: 120 }}>
          {record.completion_date && (
            <div style={{ fontSize: '12px', marginBottom: 4 }}>
              <div style={{ color: '#666', marginBottom: 2 }}>Completed</div>
              <div style={{ fontWeight: 500 }}>
                {dayjs(record.completion_date).format('DD MMM YYYY')}
              </div>
            </div>
          )}
          <div style={{ fontSize: '12px', paddingTop: record.completion_date ? 4 : 0, borderTop: record.completion_date ? '1px dashed #e8e8e8' : 'none' }}>
            <div style={{ color: '#666', marginBottom: 2 }}>Uploaded</div>
            <div style={{ fontWeight: 500 }}>
              {dayjs(record.created_at).format('DD MMM YYYY')}
            </div>
          </div>
        </div>
      ),
      width: '140px',
    },
    {
      title: activeTab === 'pending' ? 'Status' : 'Verification Info',
      key: 'verification_info',
      render: (_, record) => {
        if (activeTab === 'pending') {
          return <Tag icon={<ClockCircleOutlined />} color="orange">Pending</Tag>;
        }

        return (
          <div style={{ minWidth: 140 }}>
            {getStatusTag(record.status)}
            {record.verified_by_user && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                Verified by: {record.verified_by_user.email}
              </div>
            )}
            {record.verified_at && (
              <div style={{ fontSize: '10px', color: '#999', marginTop: 2 }}>
                {dayjs(record.verified_at).format('DD MMM, HH:mm')}
              </div>
            )}
          </div>
        );
      },
      width: '160px',
    },
    {
      title: 'Aksi',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPDF(record)}
            block
          >
            View
          </Button>
          {activeTab === 'pending' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => showVerifyModal(record)}
                block
              >
                Verify
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => showRejectModal(record)}
                block
              >
                Reject
              </Button>
            </>
          )}
          {activeTab === 'verified' && (
            <Button
              type="default"
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => showRejectModal(record)}
              block
              danger
            >
              Reject
            </Button>
          )}
          {activeTab === 'rejected' && (
            <Button
              type="primary"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => showVerifyModal(record)}
              block
            >
              Re-verify
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Calculate badge counts
  const pendingCount = certifications.filter((c) => c.status === 'pending').length;

  const content = (
    <div className="certification-verification-page">
      {!embedded && (
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Sertifikasi AI</Breadcrumb.Item>
          <Breadcrumb.Item>Verifikasi</Breadcrumb.Item>
        </Breadcrumb>
      )}

      {/* Main Content */}
      <div>
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <TabPane
              tab={
                <span>
                  <ClockCircleOutlined /> Pending{' '}
                  {pendingCount > 0 && <Badge count={pendingCount} />}
                </span>
              }
              key="pending"
            />
            <TabPane
              tab={
                <span>
                  <CheckCircleOutlined /> Verified
                </span>
              }
              key="verified"
            />
            <TabPane
              tab={
                <span>
                  <CloseCircleOutlined /> Rejected
                </span>
              }
              key="rejected"
            />
            <TabPane
              tab={
                <span>
                  <SafetyCertificateOutlined /> All
                </span>
              }
              key="all"
            />
          </Tabs>

          <Table
            columns={columns}
            dataSource={certifications}
            loading={loading}
            rowKey="id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} sertifikasi`,
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`Tidak ada sertifikasi ${activeTab === 'all' ? '' : activeTab}`}
                />
              ),
            }}
            expandable={{
              expandedRowRender: (record) => (
                <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Nama File:</strong> {record.original_filename}
                    </div>
                    <div>
                      <strong>Ukuran:</strong> {formatFileSize(record.file_size)}
                    </div>
                    {record.completion_date && (
                      <div>
                        <strong>Tanggal Selesai:</strong>{' '}
                        {dayjs(record.completion_date).format('DD MMMM YYYY')}
                      </div>
                    )}
                    {record.notes && (
                      <div>
                        <strong>Catatan User:</strong>
                        <div style={{ marginTop: '8px', color: '#666' }}>{record.notes}</div>
                      </div>
                    )}
                    {record.status === 'verified' && record.verified_by_user && (
                      <div style={{ marginTop: '8px', color: '#14B8A6' }}>
                        <strong>Diverifikasi oleh:</strong> {record.verified_by_user.email}
                        {record.verified_at && (
                          <span style={{ marginLeft: '16px' }}>
                            pada {dayjs(record.verified_at).format('DD/MM/YYYY HH:mm')}
                          </span>
                        )}
                      </div>
                    )}
                  </Space>
                </div>
              ),
              rowExpandable: (record) => record.notes || record.completion_date || record.verified_by,
            }}
          />
        </div>

        {/* Verify Modal - MANUAL NIM/NIP INPUT */}
        <Modal
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#14B8A6' }} />
              <span>Verifikasi Sertifikasi</span>
            </Space>
          }
          open={verifyModalVisible}
          onCancel={() => {
            setVerifyModalVisible(false);
            form.resetFields();
            setSelectedRole(null);
            setSelectedAffiliateUnitId(null);
          }}
          footer={null}
          width={700}
        >
          {selectedCert && (
            <div>
              {/* Certificate Info */}
              <div style={{ marginBottom: 20, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
                <p style={{ margin: '4px 0' }}><strong>User:</strong> {selectedCert.user?.name || selectedCert.user?.email}</p>
                <p style={{ margin: '4px 0' }}><strong>Judul:</strong> {selectedCert.title}</p>
                <p style={{ margin: '4px 0' }}>
                  <strong>URL:</strong> <a href={selectedCert.file_path} target="_blank" rel="noopener noreferrer">Lihat Sertifikat</a>
                </p>
              </div>

              {/* Manual NIM/NIP Input Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleVerify}
                className="verification-form"
              >
                <Form.Item
                  label="Role"
                  name="role"
                  rules={[{ required: true, message: 'Role wajib dipilih' }]}
                >
                  <Select
                    placeholder="Pilih role terlebih dahulu"
                    size="large"
                    onChange={handleRoleChange}
                  >
                    <Option value="student">Mahasiswa</Option>
                    <Option value="lecturer">Dosen</Option>
                    <Option value="staff">Tenaga Pendidik</Option>
                  </Select>
                </Form.Item>

                {selectedRole === 'student' && (
                  <Form.Item
                    label="NIM (9 digit)"
                    name="nim_nip"
                    rules={[
                      { required: true, message: 'NIM wajib diisi' },
                      { pattern: /^[0-9]{9}$/, message: 'NIM harus 9 digit angka' }
                    ]}
                  >
                    <Input
                      placeholder="Masukkan NIM 9 digit"
                      size="large"
                      maxLength={9}
                    />
                  </Form.Item>
                )}

                {(selectedRole === 'lecturer' || selectedRole === 'staff') && (
                  <>
                    <Form.Item
                      label={`Cari ${selectedRole === 'lecturer' ? 'Dosen' : 'Tenaga Pendidik'}`}
                      name="affiliate_search"
                      tooltip="Gunakan fitur pencarian untuk otomatis mengisi data dan unit"
                    >
                      <AutoComplete
                        options={affiliateOptions}
                        onSearch={handleAffiliateSearch}
                        onSelect={handleAffiliateSelect}
                        placeholder={`Ketik nama atau NIP ${selectedRole === 'lecturer' ? 'dosen' : 'tenaga pendidik'}...`}
                        size="large"
                        notFoundContent={searchingAffiliates ? <Spin size="small" /> : 'Tidak ada data'}
                      />
                    </Form.Item>

                    <div style={{
                      background: '#e6f7ff',
                      padding: '8px 12px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      border: '1px solid #91d5ff',
                      fontSize: '13px'
                    }}>
                      💡 <strong>Tips:</strong> Gunakan pencarian di atas untuk otomatis mengisi NIP, Nama, dan Unit.
                      Jika tidak ditemukan, Anda dapat mengisi manual di bawah.
                    </div>

                    <Form.Item
                      label="NIP"
                      name="nim_nip"
                      rules={[
                        { required: true, message: 'NIP wajib diisi' },
                        { pattern: /^[0-9]+$/, message: 'Hanya boleh angka' }
                      ]}
                    >
                      <Input
                        placeholder="NIP akan terisi otomatis atau ketik manual"
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      label="Nama"
                      name="name"
                      rules={[{ required: true, message: 'Nama wajib diisi' }]}
                    >
                      <Input
                        placeholder="Nama akan terisi otomatis atau ketik manual"
                        size="large"
                      />
                    </Form.Item>
                  </>
                )}

                <div style={{
                  background: '#e6f7ff',
                  padding: '12px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  border: '1px solid #91d5ff'
                }}>
                  <div style={{ fontSize: '13px', color: '#0050b3' }}>
                    ℹ️ <strong>Scoring Otomatis:</strong> Setiap sertifikasi yang disetujui akan mendapat +1 poin secara otomatis.
                  </div>
                </div>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={() => {
                      setVerifyModalVisible(false);
                      form.resetFields();
                      setSelectedRole(null);
                    }}>
                      Batal
                    </Button>
                    <Button type="primary" htmlType="submit" style={{ background: '#14B8A6' }}>
                      Verifikasi & Berikan +1 Poin
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </div>
          )}

        </Modal>

        {/* Reject Modal */}
        <Modal
          title={
            <Space>
              <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              <span>Tolak Sertifikasi</span>
            </Space>
          }
          open={rejectModalVisible}
          onCancel={() => setRejectModalVisible(false)}
          footer={null}
        >
          {selectedCert && (
            <Space direction="vertical" style={{ width: '100%' }}>
              <p>
                <strong>User:</strong> {selectedCert.user?.name || selectedCert.user_id}
              </p>
              <p>
                <strong>Judul:</strong> {selectedCert.title}
              </p>
              <Form form={rejectForm} layout="vertical" onFinish={handleReject}>
                <Form.Item
                  name="rejection_reason"
                  label="Alasan Penolakan"
                  rules={[
                    { required: true, message: 'Silakan masukkan alasan penolakan' },
                    { min: 10, message: 'Alasan minimal 10 karakter' },
                    { max: 500, message: 'Alasan maksimal 500 karakter' },
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Contoh: Sertifikat tidak jelas, nama tidak sesuai, bukan dari Google, dll."
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Space>
                    <Button type="primary" danger htmlType="submit">
                      Tolak Sertifikasi
                    </Button>
                    <Button onClick={() => setRejectModalVisible(false)}>Batal</Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          )}
        </Modal>
    </div>
  );

  return embedded ? content : (
    <AppLayout activeMenu="/certifications/verify">
      {content}
    </AppLayout>
  );
};

export default CertificationVerification;
